import React, { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Calendar as CalendarIcon, Home, Users, DollarSign, FileText, Wallet } from "lucide-react";
import AuthWrapper from "./components/AuthWrapper";
import ErrorBoundary from "./components/ErrorBoundary";
import OwnerReport from "./components/OwnerReport";
import * as cacheManager from "./utils/cacheManager";

// Import components
import Calendar from "./components/Calendar";
import Properties from "./components/Properties";
import Cleaners from "./components/Cleaners";
import Payouts from "./components/Payouts";
import Reports from "./components/Reports";
import Budget from "./components/Budget";

// Cache configuration - aligns with hourly cron job
const CACHE_KEY = 'app_bootstrap_cache';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour in milliseconds

// Cache utilities
const getCache = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp, lastSync } = JSON.parse(cached);
    const age = Date.now() - timestamp;

    // Cache is valid if less than 1 hour old
    if (age < CACHE_TTL) {
      return { data, timestamp, lastSync, isStale: false };
    }

    // Return stale data but mark it as stale (will trigger background refresh)
    return { data, timestamp, lastSync, isStale: true };
  } catch (e) {
    console.error('Error reading bootstrap cache:', e);
    return null;
  }
};

const setCache = (data, lastSync) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now(),
      lastSync
    }));
  } catch (e) {
    console.error('Error writing bootstrap cache:', e);
  }
};

const clearCache = () => {
  localStorage.removeItem(CACHE_KEY);
};

// App layout with tab navigation
const AppLayout = ({
  currentView,
  setCurrentView,
  properties,
  cleaners,
  owners,
  handleUpdateProperties,
  handleDeleteProperty,
  refreshBootstrap,
  preloadStatus
}) => {
  const navItems = [
    { id: 'calendar', label: 'Calendar', icon: CalendarIcon },
    { id: 'properties', label: 'Properties', icon: Home },
    { id: 'cleaners', label: 'Cleaners', icon: Users },
    { id: 'payouts', label: 'Payouts', icon: DollarSign },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'budget', label: 'Budget', icon: Wallet },
  ];

  const [visibleView, setVisibleView] = useState(currentView);
  const [contentVisible, setContentVisible] = useState(true);

  const handleNavClick = useCallback((id) => {
    if (id === visibleView) return;
    setContentVisible(false);
    setTimeout(() => {
      setCurrentView(id);
      setVisibleView(id);
      setContentVisible(true);
    }, 180);
  }, [visibleView, setCurrentView]);

  const isScrollable = visibleView === 'payouts' || visibleView === 'budget';

  return (
    <div className="flex flex-col h-screen bg-zinc-950">
      <header className="bg-zinc-900/80 backdrop-blur-md border-b border-zinc-800 px-6 py-6 flex flex-col sm:flex-row gap-4 sm:gap-0 sm:justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
            <h1 className="text-3xl font-bold text-zinc-100 tracking-tight whitespace-nowrap flex items-center gap-1">
              <img src="/logo.svg" alt="G" className="w-9 h-10 flex-shrink-0 drop-shadow-lg" />Homes
            </h1>
            <span className="text-3xl font-bold text-violet-400 tracking-tight whitespace-nowrap">Realty Inc.</span>
          </div>
        </div>
        <nav className="flex gap-1 w-full sm:w-auto justify-end">
          {navItems.map(({ id, label, icon: Icon }) => {
            const isActive = visibleView === id;
            return (
              <button
                key={id}
                onClick={() => handleNavClick(id)}
                className={`px-4 py-2 rounded-lg transition-all flex flex-col items-center justify-center min-w-[3rem]
                  ${isActive
                    ? "bg-zinc-800 text-zinc-100"
                    : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/60"
                  }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? "text-violet-400" : ""}`} />
                <span
                  className="text-[10px] font-medium mt-0.5 leading-none transition-all duration-200 overflow-hidden"
                  style={{
                    opacity: isActive ? 1 : 0,
                    maxHeight: isActive ? '14px' : '0px',
                  }}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </nav>
      </header>

      <main className={`flex-1 p-3 ${isScrollable ? 'overflow-y-auto' : 'overflow-hidden'}`}>
        <div
          className={`${isScrollable ? 'min-h-full' : 'h-full'} bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden transition-opacity duration-180`}
          style={{ opacity: contentVisible ? 1 : 0, transition: 'opacity 0.18s ease-in-out' }}
        >
          {visibleView === "calendar" && (
            <Calendar properties={properties} cleaners={cleaners} />
          )}
          {visibleView === "properties" && (
            <Properties
              properties={properties}
              onUpdateProperties={handleUpdateProperties}
              onDeleteProperty={handleDeleteProperty}
            />
          )}
          {visibleView === "cleaners" && (
            <Cleaners
              cleaners={cleaners}
              properties={properties}
              onCleanersChange={refreshBootstrap}
            />
          )}
          {visibleView === "payouts" && (
            <Payouts cleaners={cleaners} properties={properties} owners={owners} preloaded={preloadStatus.payouts} />
          )}
          {visibleView === "reports" && (
            <Reports properties={properties} preloaded={preloadStatus.reports} />
          )}
          {visibleView === "budget" && (
            <Budget />
          )}
        </div>
      </main>
    </div>
  );
};

const CleanerCalendarView = ({ properties, cleaners }) => {
  return (
    <div className="h-screen bg-zinc-950">
      <div className="h-full flex flex-col">
        <main className="flex-1 p-3 overflow-hidden">
          <div className="h-full bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
            <Calendar properties={properties} cleaners={cleaners} />
          </div>
        </main>
      </div>
    </div>
  );
};

const App = () => {
  const [currentView, setCurrentView] = useState("calendar");
  const [properties, setProperties] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [owners, setOwners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Preload status tracking
  const [preloadStatus, setPreloadStatus] = useState({
    payouts: false,
    reports: false
  });
  const preloadStarted = useRef(false);

  // Fetch bootstrap data from server
  const fetchBootstrap = useCallback(async () => {
    try {
      console.log('[App] Fetching bootstrap data from server...');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bootstrap`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch bootstrap data");
      }

      const data = await response.json();
      console.log(`[App] Bootstrap loaded: ${data.properties?.length || 0} properties, ${data.cleaners?.length || 0} cleaners, ${data.owners?.length || 0} owners`);

      // Update state
      setProperties(Array.isArray(data.properties) ? data.properties : []);
      setCleaners(Array.isArray(data.cleaners) ? data.cleaners : []);
      setOwners(Array.isArray(data.owners) ? data.owners : []);

      // Update cache
      setCache(data, data.lastSync);

      return data;
    } catch (error) {
      console.error("[App] Error fetching bootstrap:", error);
      throw error;
    }
  }, []);

  // Check if server has newer data than our cache
  const checkForUpdates = useCallback(async (cachedLastSync) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/bootstrap/check-update?since=${cachedLastSync}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
        }
      );

      if (!response.ok) return true; // Assume update needed if check fails

      const { hasUpdate } = await response.json();
      return hasUpdate;
    } catch (error) {
      console.error("[App] Error checking for updates:", error);
      return true; // Assume update needed on error
    }
  }, []);

  // Refresh bootstrap (used after cleaners/properties changes)
  const refreshBootstrap = useCallback(async () => {
    clearCache();
    await fetchBootstrap();
  }, [fetchBootstrap]);

  // Preload Payouts and Reports data after Calendar loads
  const preloadOtherTabs = useCallback(async () => {
    if (preloadStarted.current) return;
    preloadStarted.current = true;

    // Initialize cache manager (prunes old data)
    cacheManager.initializeCache();

    console.log('[App] Starting background preload of Payouts and Reports...');

    // 1. Preload Payouts (current week ± 2 weeks)
    try {
      const weekOffsets = [0, -1, 1, -2, 2];
      for (const offset of weekOffsets) {
        const weekKey = cacheManager.getWeekKey(offset);
        // Skip if already cached
        if (cacheManager.getPayoutSlots(weekKey)) continue;

        const weekStart = cacheManager.getWeekStart(offset);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/reservations/payout-slots?weekStart=${weekStart.toISOString()}`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
          }
        );
        if (response.ok) {
          const data = await response.json();
          cacheManager.setPayoutSlots(weekKey, data);
        }
      }
      setPreloadStatus(prev => ({ ...prev, payouts: true }));
      console.log('[App] Payouts preloaded');
    } catch (error) {
      console.error('[App] Payouts preload failed:', error);
    }

    // 2. Preload Reports (current month ± 3 months)
    try {
      const monthOffsets = [0, -1, 1, -2, 2, -3, 3];
      const startMonth = cacheManager.getMonthKey(Math.min(...monthOffsets));
      const endMonth = cacheManager.getMonthKey(Math.max(...monthOffsets));

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reservations/report-slots?startMonth=${startMonth}&endMonth=${endMonth}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Cache each month separately
        if (data.slots) {
          Object.keys(data.slots).forEach(monthKey => {
            cacheManager.setReportSlots(monthKey, data.slots[monthKey]);
          });
        }
      }
      setPreloadStatus(prev => ({ ...prev, reports: true }));
      console.log('[App] Reports preloaded');
    } catch (error) {
      console.error('[App] Reports preload failed:', error);
    }

    console.log('[App] Background preload complete');
  }, []);

  // Handle property updates
  const handleUpdateProperties = useCallback(async (updatedProperties) => {
    try {
      await Promise.all(
        updatedProperties.map(async (property) => {
          let response;
          if (property._id) {
            response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/properties/${property._id}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
                },
                body: JSON.stringify(property),
              }
            );
          } else {
            response = await fetch(`${process.env.REACT_APP_API_URL}/api/properties`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
              },
              body: JSON.stringify(property),
            });
          }
          if (!response.ok) {
            throw new Error(`Failed to save property "${property.name}" (${response.status})`);
          }
        })
      );

      // Refresh bootstrap to get updated data
      await refreshBootstrap();
    } catch (error) {
      console.error("Error updating properties:", error);
    }
  }, [refreshBootstrap]);

  // Handle property deletion
  const handleDeleteProperty = useCallback(async (propertyId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/properties/${propertyId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete property");
      }

      await refreshBootstrap();
    } catch (error) {
      console.error("Error deleting property:", error);
    }
  }, [refreshBootstrap]);

  // Initial data load with smart caching
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);

      try {
        // Step 1: Check cache
        const cached = getCache();

        if (cached && cached.data) {
          // Show cached data immediately
          console.log('[App] Using cached bootstrap data (age: ' + Math.round((Date.now() - cached.timestamp) / 1000 / 60) + ' min)');
          setProperties(Array.isArray(cached.data.properties) ? cached.data.properties : []);
          setCleaners(Array.isArray(cached.data.cleaners) ? cached.data.cleaners : []);
          setOwners(Array.isArray(cached.data.owners) ? cached.data.owners : []);
          setIsLoading(false);

          // If cache is stale, check if server has updates
          if (cached.isStale) {
            console.log('[App] Cache is stale, checking for server updates...');
            const needsUpdate = await checkForUpdates(cached.lastSync || cached.timestamp);

            if (needsUpdate) {
              console.log('[App] Server has updates, clearing caches and refreshing...');
              cacheManager.clearAllCaches(); // Clear payout/report caches too
              // Run in background; catch so a failed refresh doesn't surface
              // as an unhandled rejection (stale data is already displayed)
              fetchBootstrap().catch(err =>
                console.error('[App] Background bootstrap refresh failed:', err)
              );
            } else {
              console.log('[App] No server updates, extending cache...');
              setCache(cached.data, cached.lastSync); // Extend cache
            }
          }
        } else {
          // No cache - fetch from server
          console.log('[App] No cache found, fetching from server...');
          await fetchBootstrap();
          setIsLoading(false);
        }
      } catch (error) {
        console.error("[App] Error loading data:", error);
        setIsLoading(false);
      }
    };

    loadData();
  }, [fetchBootstrap, checkForUpdates]);

  // Trigger preload after initial data is loaded and Calendar is ready
  useEffect(() => {
    if (!isLoading && properties.length > 0 && !preloadStarted.current) {
      // Wait a moment for Calendar to render, then start preloading
      const timer = setTimeout(() => {
        preloadOtherTabs();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, properties.length, preloadOtherTabs]);

  // Show loading state
  if (isLoading && properties.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center bg-zinc-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-500 mx-auto mb-4"></div>
          <p className="text-zinc-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary fallbackMessage="The application encountered an error. Please refresh the page.">
      <Router>
        <AuthWrapper>
          <Routes>
            {/* Specific routes first */}
            <Route
              path="/calendar/:cleanerId"
              element={
                <ErrorBoundary fallbackMessage="Error loading cleaner calendar.">
                  <CleanerCalendarView properties={properties} cleaners={cleaners} />
                </ErrorBoundary>
              }
            />
            <Route
              path="/owner-report/:encoded"
              element={
                <ErrorBoundary fallbackMessage="Error loading owner report.">
                  <OwnerReport />
                </ErrorBoundary>
              }
            />

            {/* Main app routes */}
            <Route
              path="/"
              element={
                <ErrorBoundary fallbackMessage="Error loading application.">
                  <AppLayout
                    currentView={currentView}
                    setCurrentView={setCurrentView}
                    properties={properties}
                    cleaners={cleaners}
                    owners={owners}
                    handleUpdateProperties={handleUpdateProperties}
                    handleDeleteProperty={handleDeleteProperty}
                    refreshBootstrap={refreshBootstrap}
                    preloadStatus={preloadStatus}
                  />
                </ErrorBoundary>
              }
            />

            {/* Redirect any other paths to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthWrapper>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
